import { Loader2, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RequestCommissionEditDialog } from "@/pages/commission/RequestCommissionEditDialog";
import { ReassignResponsibleAgentDialog } from "@/components/ReassignResponsibleAgentDialog";
import { commissionApi, type CommissionTransaction } from "@/lib/commission.api";
import type { Party } from "@/lib/party.api";
import { getPartyName } from "../commission-calculations";
import {
  formatDate,
  formatDetailValue,
  formatMoney,
  getCommissionAmount,
} from "../commission-formatters";
import { CommissionPendingBadge } from "./CommissionPendingBadge";
import { DetailStat } from "./CommissionDetailPrimitives";

interface CommissionDetailDialogProps {
  commission: CommissionTransaction | null;
  parties: Record<number, Party>;
  canDelete: boolean;
  canReassign: boolean;
  deletingId: number | null;
  typeLabel: (code: string) => string;
  onOpenChange: (open: boolean) => void;
  onReload: () => void;
  onDelete: (id: number) => void;
}

export function CommissionDetailDialog({
  commission,
  parties,
  canDelete,
  canReassign,
  deletingId,
  typeLabel,
  onOpenChange,
  onReload,
  onDelete,
}: CommissionDetailDialogProps) {
  const client = commission ? getPartyName(parties, commission.clientPartyId) : undefined;
  const referrer = commission ? getPartyName(parties, commission.referrerPartyId) : undefined;
  const detailEntries = Object.entries(commission?.details ?? {});

  return (
    <Dialog open={Boolean(commission)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] overflow-y-auto bg-card sm:max-w-3xl">
        {commission && (
          <>
            <DialogHeader>
              <div className="flex flex-col gap-3 pr-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <DialogTitle className="text-[17px] font-bold text-foreground">
                    Commission #{commission.id}
                  </DialogTitle>
                  <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
                    {typeLabel(commission.type)} - agent #{commission.agentId}
                  </DialogDescription>
                </div>
                {commission.pendingEditRequestId && <CommissionPendingBadge long />}
              </div>
            </DialogHeader>

            <div className="grid gap-3 sm:grid-cols-4">
              <DetailStat label="Date" value={formatDate(commission.date)} />
              <DetailStat
                label="Montant"
                value={formatMoney(getCommissionAmount(commission))}
              />
              <DetailStat label="Client" value={client ?? "-"} />
              <DetailStat label="Référent" value={referrer ?? "-"} />
            </div>

            {commission.note && (
              <div className="rounded-lg border border-border bg-surface-muted px-4 py-3">
                <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Note
                </p>
                <p className="mt-1 text-[12.5px] leading-relaxed text-body">{commission.note}</p>
              </div>
            )}

            <div className="overflow-hidden rounded-lg border border-border">
              <div className="border-b border-border bg-surface-muted px-4 py-2">
                <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Détails spécifiques
                </p>
              </div>
              {detailEntries.length === 0 ? (
                <p className="px-4 py-5 text-center text-[12px] text-muted-foreground">
                  Aucun détail spécifique.
                </p>
              ) : (
                <table className="w-full text-left">
                  <tbody>
                    {detailEntries.map(([key, value]) => (
                      <tr key={key} className="border-b border-border last:border-0">
                        <td className="w-1/3 px-4 py-2 text-[12px] font-medium text-body">
                          {key}
                        </td>
                        <td className="px-4 py-2 text-[12px] text-muted-foreground">
                          {formatDetailValue(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <DetailStat label="Créée le" value={formatDate(commission.createdAt)} />
              <DetailStat label="Statut" value={commission.active ? "Active" : "Supprimée"} />
              <DetailStat
                label="Demande"
                value={commission.pendingEditRequestId ? `#${commission.pendingEditRequestId}` : "-"}
              />
            </div>

            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Fermer
              </Button>
              {!commission.pendingEditRequestId && (
                <span onClick={(event) => event.stopPropagation()}>
                  <RequestCommissionEditDialog commission={commission} onRequested={onReload} />
                </span>
              )}
              {canReassign && (
                <span onClick={(event) => event.stopPropagation()}>
                  <ReassignResponsibleAgentDialog
                    currentResponsibleAgentId={commission.responsibleAgentId ?? commission.agentId}
                    onReassign={(newId, reason) =>
                      commissionApi
                        .reassignResponsibleAgent(commission.id, newId, reason)
                        .then(() => onReload())
                    }
                  />
                </span>
              )}
              {canDelete && (
                <Button
                  variant="destructive"
                  onClick={() => onDelete(commission.id)}
                  disabled={deletingId === commission.id}
                >
                  {deletingId === commission.id ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Trash2 size={13} />
                  )}
                  Supprimer
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
